import axios, { AxiosError, AxiosResponse } from 'axios';
import { ref } from 'vue';
import { Messages } from '../../message/message-types';
import useMessage from '../../message/use-message';
import { useRootStore } from '../../root/root-store';
import { HttpStatusCode } from '../http-status-code';
import {
  httpForbiddenHandler,
  httpInternalServerErrorHandler,
  httpUnprocessableEntityHandler,
} from './http-error-handler';

export const httpInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string,
  withCredentials: true,
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function useHttpService() {
  const config = ref<any | null>(null);
  const rootStore = useRootStore();

  httpInstance.interceptors.request.use((config) => {
    if (
      config.method === HttpMethod.POST ||
      config.method === HttpMethod.PUT ||
      config.method === HttpMethod.DELETE
    ) {
      setTimeout(() => rootStore.setAutoSaveProgress(false), 4000);
    }

    return config;
  });

  const httpSetConfig = (newConfig: any): void => {
    config.value = newConfig;
  };

  const httpGet = async <T>(path: string, data: any = {}): Promise<T> => {
    return send(HttpMethod.GET, path, data);
  };

  const httpPost = async <T>(
    path: string,
    data: any,
    messages: Messages | null = null
  ): Promise<T> => {
    const dataToSend: any = { ...data };

    return send(HttpMethod.POST, path, dataToSend, messages);
  };

  const httpPut = async <T>(
    path: string,
    data: any,
    messages: Messages | null = null
  ): Promise<T> => {
    const dataToSend: any = { ...data };

    return send(HttpMethod.PUT, path, dataToSend, messages);
  };

  const httpDelete = async <T>(
    path: string,
    data: any,
    messages: Messages | null = null
  ): Promise<T> => {
    const dataToSend: any = { ...data };
    return send(HttpMethod.DELETE, path, dataToSend, messages);
  };

  const httpUpload = async <T>(
    path: string,
    file: File,
    keyName = 'file',
    additionalData?: {
      [key in string]: string | boolean | number | null | undefined;
    }
  ): Promise<T> => {
    const formData = new FormData();
    formData.append(keyName, file);

    if (additionalData) {
      for (const key in additionalData) {
        formData.set(key, additionalData[key] as string);
      }
    }

    httpSetConfig({
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return send(HttpMethod.POST, path, formData, null);
  };

  const send = async (
    method: string,
    path: string,
    data: any = {},
    messages: Messages | null = null
  ) => {
    const { clearMessages, setSuccessMessage } = useMessage();
    clearMessages();

    if (
      method === HttpMethod.POST ||
      method === HttpMethod.PUT ||
      method === HttpMethod.DELETE
    ) {
      rootStore.setAutoSaveProgress(true);
    }

    let request = null;
    switch (method) {
      case 'get':
        request = httpInstance.get(path, data);
        break;
      case 'post':
        request = httpInstance.post(path, data, config.value);
        break;
      case 'put':
        request = httpInstance.put(path, data);
        break;
      case 'delete':
        request = httpInstance.delete(path, data);
        break;
    }

    if (request === null) {
      throw new Error(`Unsupported request method: ${method}`);
    }

    request.catch((error: AxiosError) => {
      if (!error.response) {
        return;
      }

      handleError(error.response);
    });

    const response = await request;
    const result = response.data;

    if (messages !== null) {
      setSuccessMessage(messages.success as string);
    }

    rootStore.setAutoSaveProgress(false);
    return result;
  };

  const handleError = (errorResponse: AxiosResponse): void => {
    switch (errorResponse.status as HttpStatusCode) {
      case HttpStatusCode.FORBIDDEN:
      case HttpStatusCode.UNAUTHORIZED:
        httpForbiddenHandler(errorResponse);
        break;
      case HttpStatusCode.UNPROCESSABLE_ENTITY:
        httpUnprocessableEntityHandler(errorResponse);
        break;
      case HttpStatusCode.INTERNAL_SERVER_ERROR:
        httpInternalServerErrorHandler(errorResponse);
        break;
      default:
        throw new Error(errorResponse.statusText);
    }
  };

  return {
    httpGet,
    httpPost,
    httpPut,
    httpDelete,
    httpSetConfig,
    httpUpload,
    handleError,
  };
}

class HttpMethod {
  public static readonly GET = 'get';
  public static readonly POST = 'post';
  public static readonly PUT = 'put';
  public static readonly DELETE = 'delete';
}
